import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useToast } from 'src/context/ToastProvider';
import { useMutation, useQueryClient } from 'react-query';
import { Button, FileInput, Label, Modal, Spinner } from 'flowbite-react';
import { getPolicyForS3NonVideo, updateUserById } from 'src/apis/user';
import { useUserContext } from 'src/context/UserProvider';
import { handleS3Upload } from 'src/utils/imageUpload';
import { get } from 'lodash';
import Webcam from 'react-webcam';
import { HiCamera, HiOutlineArrowRight } from 'react-icons/hi';
import { useStartMeetingMutation } from 'src/hooks/utils/useStartMeetingMutation.hooks';
import moment from 'moment';
import { useNavigate, useSearchParams } from 'react-router-dom';
import BrowserWarningModal from 'src/components/scheduleMeetingDrawer/BrowserWarningModal';
import { browserName, isMobile } from 'react-device-detect';
import { APP_ENDPOINT } from 'src/config/env.config';
import classNames from 'classnames';
import NewCreatingPositionLoader from 'src/components/position/NewCreatingPositionLoader';
import Select, { SingleValue } from 'react-select';

async function dataURLToFile(dataUrl: string, fileName: string): Promise<File> {
	const response = await fetch(dataUrl);
	const blob = await response.blob();
	return new File([blob], fileName, { type: blob.type });
}

function fileToDataURL(file: File): Promise<string> {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = () => resolve(reader.result as string);
		reader.onerror = error => reject(error);
		reader.readAsDataURL(file);
	});
}

const TakeProfilePhoto = ({
	isModal,
	userId,
	onFinish,
}: {
	isModal?: boolean;
	userId?: string;
	onFinish?: () => void;
}) => {
	const [file, setFile] = useState<File | null>();
	const toast = useToast();
	const { user } = useUserContext();
	const queryClient = useQueryClient();
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [searchParams, setSearchParams] = useSearchParams();
	const roundTemplateId = searchParams.get('roundTemplateId');
	const [img, setImg] = useState<string | null>(null);
	const webcamRef = useRef<Webcam | null>(null);
	const { openBrowserWarningModal, setOpenBrowserWarningModal } = useUserContext();
	const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
	const [selectedDeviceId, setSelectedDeviceId] = useState<{ label: string; value: string } | undefined>(undefined);
	const videoConstraints = {
		facingMode: 'user',
		aspectRatio: 16 / 9,
		deviceId: selectedDeviceId ? { exact: selectedDeviceId.value } : undefined,
	};

	const handleDevices = useCallback(
		(mediaDevices: MediaDeviceInfo[]) => setDevices(mediaDevices.filter(({ kind }) => kind === 'videoinput')),
		[setDevices],
	);

	useEffect(() => {
		navigator.mediaDevices.enumerateDevices().then(handleDevices);
	}, [handleDevices]);

	useEffect(() => {
		const getDevices = async () => {
			const allDevices = await navigator.mediaDevices.enumerateDevices();
			const videoDevices = allDevices.filter(device => device.kind === 'videoinput');
			setDevices(videoDevices);
			if (videoDevices.length > 0) {
				setSelectedDeviceId({
					label: videoDevices[0].label || `Camera ${videoDevices[0].deviceId}`,
					value: videoDevices[0].deviceId,
				}); // Default to the first device
			}
		};

		getDevices();
	}, []);

	const capture = useCallback(async () => {
		if (webcamRef?.current) {
			const imageSrc = webcamRef?.current?.getScreenshot();

			setImg(imageSrc);

			if (imageSrc) {
				const file = await dataURLToFile(imageSrc, 'proctoringImage.png');
				setFile(file);
			}
		}
	}, [webcamRef?.current]);

	const handleSuccess = () => {
		// push APP_ENDPOINT in browser history
		if (!isModal) {
			try {
				window.history.pushState({}, '', APP_ENDPOINT);
			} catch (error) {
				console.log('error:', error);
			}
		}

		// start test
		if (roundTemplateId) {
			if (browserName !== 'Chrome' || isMobile) {
				setOpenBrowserWarningModal(true);
				return;
			}
			startMeetingForRoundTemplateMutation.mutate({
				roundTemplateId,
				isMock: false,
			});
		} else if (!isModal) {
			toast({
				title: 'Round template not found, try again',
				type: 'error',
				duration: 3000,
			});
			navigate('/');
		} else if (isModal) {
			onFinish && onFinish();
		}

		toast({
			title: 'Profile image updated successfully',
			type: 'success',
			duration: 3000,
		});
		setIsLoading(false);
	};

	const hiddenFileInput = useRef<HTMLInputElement | null>(null);
	const navigate = useNavigate();
	const startMeetingForRoundTemplateMutation = useStartMeetingMutation();
	const updateProfileByIdFn = useMutation(['updateUserById'], updateUserById, {
		onSuccess() {
			handleSuccess();
		},
		onError(error: Error) {
			const errorMessage = get(error, ['response', 'data', 'message'], error.message);
			toast({
				type: 'error',
				title: errorMessage,
			});
			setIsLoading(false);
		},
	});

	const handleSubmitUser = async () => {
		if (file) {
			const fileSizeMB = file.size / (1024 * 1024); // Convert bytes to MB
			if (fileSizeMB > 5) {
				toast({
					type: 'error',
					title: 'File size exceeds the maximum limit of 5 MB',
					duration: 10000,
				});
				return;
			}
			setIsLoading(true);
			let imageUrl = '';
			if (file && (user || userId)) {
				// fileName, fileLocation, mime
				const policy = await getPolicyForS3NonVideo(
					file.name,
					'profileImages',
					file.type,
					true,
					localStorage.getItem('guestUserJwtToken') || '',
				);
				const uploadedUrl = await handleS3Upload(policy, file);
				if (uploadedUrl) imageUrl = uploadedUrl;
				if (imageUrl) {
					const id = userId || String(user?._id) || '';
					updateProfileByIdFn.mutate({
						id: id,
						data: {
							profile: {
								proctoringImageUrl: imageUrl,
							},
						},
						token: localStorage.getItem('guestUserJwtToken') || '',
					});
				}
			} else {
				setIsLoading(false);
			}
		}
	};

	const [isWebcamReady, setIsWebcamReady] = useState(false);

	useEffect(() => {
		if (webcamRef?.current) {
			setIsWebcamReady(true);
		}
	}, [webcamRef?.current]);

	useEffect(() => {
		if (user?.flags?.isImageVerified) {
			handleSuccess();
		}
	}, [user?.flags?.isImageVerified]);

	if (user?.flags?.isImageVerified) {
		return (
			<div className="w-full h-full flex-1 flex justify-center items-center">
				<Spinner size="xl" />
			</div>
		);
	}

	return (
		<>
			<div
				className={classNames(
					'backdrop-blur-[4px] flex flex-col justify-center',
					isModal ? '' : 'h-[calc(100vh-140px)] px-4 md:h-[calc(100vh-70px)]',
				)}
			>
				<div className="dark:bg-[#1F2A37] p-0">
					{img === null ? (
						<div className="flex justify-center flex-col gap-4 items-center pb-3 max-w-[500px] mx-auto w-full">
							<Label className="text-base">Select Camera</Label>
							<div className="w-full">
								<Select
									menuPlacement={'auto'}
									classNamePrefix="my-select-plans"
									options={devices.map(device => ({
										label: device.label || `Camera ${device.deviceId}`,
										value: device.deviceId,
									}))}
									onChange={(e: SingleValue<{ label: string; value: string }>) => {
										setSelectedDeviceId(e || undefined);
									}}
									value={selectedDeviceId}
								/>
							</div>
						</div>
					) : null}
					<div className="w-full mx-auto">
						{img === null ? (
							<>
								{!isWebcamReady ? (
									<div className="flex items-center justify-center w-full h-full">
										{/* <Spinner size="xl" /> */}
										<NewCreatingPositionLoader
											showAnimation={true}
											progressBar={false}
											customClassCss="w-[70%] md:!w-[30%] mx-auto "
										/>
									</div>
								) : null}
								<div
									className={classNames(
										'w-full h-full max-w-[960px] max-h-[540px] mx-auto  flex flex-col items-center justify-center rounded-[8px] overflow-hidden',
										isModal ? '' : 'md:min-h-[540px]',
									)}
								>
									<Webcam
										audio={false}
										mirrored={true}
										height={'100%'}
										width={'100%'}
										ref={webcamRef}
										imageSmoothing={false}
										screenshotFormat="image/jpeg"
										videoConstraints={videoConstraints}
									/>
								</div>
							</>
						) : (
							<>
								<h4 className="text-center text-xl font-medium pb-4">Preview</h4>

								<img
									src={img}
									alt="screenshot"
									width="100%"
									height="100%"
									className="rounded-[8px] w-full h-full object-cover object-center max-w-[960px] mx-auto max-h-[540px] md:min-h-[540px]"
								/>
							</>
						)}
					</div>
				</div>
				<div className="rounded-b-md py-3 dark:bg-[#18222D]">
					<div className="flex w-full justify-center gap-3 items-center flex-wrap">
						{!img ? (
							<>
								{/* <Button
								size="0"
								color="light"
								className="w-full max-w-[144px] [&_*]:w-full cursor-pointer p-0 focus-visible:outline-blue-300 "
							>
								<label htmlFor="file-input" className="cursor-[inherit] py-2 px-4">
									<input
										hidden
										type="file"
										accept="image/jpg"
										id="file-input"
										ref={hiddenFileInput}
										onChange={async e => {
											if (e?.target?.files?.length) {
												setFile(e?.target?.files[0]);
												const image = await fileToDataURL(e?.target?.files[0]);
												setImg(image);
											}
										}}
									/>
									Upload
								</label>
							</Button> */}

								<Button color="purplePlaced" size="lg" className="w-full max-w-[250px]" onClick={capture}>
									Capture & Proceed <HiOutlineArrowRight className="ml-2" />
								</Button>
							</>
						) : (
							<div className="flex w-full justify-center gap-3 items-center flex-wrap max-w-[450px] mx-auto">
								<Button
									outline
									size="lg"
									color="light"
									className="w-full flex-1"
									onClick={() => {
										setImg(null);
										setFile(null);
										setIsLoading(false);
									}}
								>
									<HiCamera className="mr-2 text-[#7777E7]" size={24} />
									Retake
								</Button>
								<Button
									size="lg"
									color="purplePlaced"
									className="bg-[#7777E7] w-full flex-1"
									disabled={isLoading || startMeetingForRoundTemplateMutation?.isLoading}
									isProcessing={isLoading || startMeetingForRoundTemplateMutation?.isLoading}
									onClick={handleSubmitUser}
								>
									Proceed <HiOutlineArrowRight className="ml-2" />
								</Button>
							</div>
						)}
					</div>
				</div>
			</div>
			<BrowserWarningModal
				openModal={openBrowserWarningModal}
				setOpenModal={() => {
					setOpenBrowserWarningModal(false);
				}}
				isJoinedFromMobile={isMobile}
			/>
		</>
	);
};

export default TakeProfilePhoto;
